/**
 * La Tienda — what XP buys. Shared by the Worker (which checks the
 * price and the balance: CLAUDE.md rule 14) and the web app (which draws it).
 *
 * The currency is "XP". A student's LEVEL comes from everything they
 * ever earned; their WALLET is earned minus spent, so buying a hat never
 * costs anybody their level.
 */
import type { Text } from './classroom'

export type Slot = 'hat' | 'hair' | 'glasses' | 'extra' | 'color' | 'bg' | 'ride'

/** `base`: a colour variant that needs the base item first (80s shades in green, say). */
export interface Item { id: string; slot: Slot; price: number; title: Text; base?: string; exclusive?: boolean }

export const SLOTS: { slot: Slot; title: Text }[] = [
  { slot: 'hat', title: { es: 'Sombreros', en: 'Hats' } },
  { slot: 'hair', title: { es: 'Pelo y pelucas', en: 'Hair & wigs' } },
  { slot: 'glasses', title: { es: 'Gafas', en: 'Glasses' } },
  { slot: 'extra', title: { es: 'Accesorios', en: 'Extras' } },
  { slot: 'color', title: { es: 'Colores', en: 'Colors' } },
  { slot: 'bg', title: { es: 'Fondos', en: 'Backgrounds' } },
  { slot: 'ride', title: { es: 'Paseos', en: 'Rides' } },
]

/*
 * Prices are tiered on purpose: the everyday stuff is cheap, the flashy stuff
 * is a goal. Saving up for the Fuegos artificiales background is budgeting.
 */
export const ITEMS: Item[] = [
  { id: 'flor', slot: 'hat', price: 150, title: { es: 'Flor de flamboyán', en: 'Flamboyán flower' } },
  { id: 'gorra', slot: 'hat', price: 300, title: { es: 'Gorra', en: 'Cap' } },
  { id: 'bandana', slot: 'hat', price: 450, title: { es: 'Bandana de PR', en: 'PR bandana' } },
  { id: 'pava', slot: 'hat', price: 700, title: { es: 'Pava jíbara', en: 'Jíbaro straw hat' } },
  { id: 'audifonos', slot: 'hat', price: 950, title: { es: 'Audífonos', en: 'Headphones' } },
  { id: 'corona', slot: 'hat', price: 2500, title: { es: 'Corona', en: 'Crown' } },
  // Not for sale: every badge at gold.
  { id: 'campeon', slot: 'hat', price: 0, exclusive: true, title: { es: 'Corona de Campeón', en: "Champion's Crown" } },

  { id: 'nerd', slot: 'glasses', price: 150, title: { es: 'Espejuelos', en: 'Specs' } },
  { id: 'gafas', slot: 'glasses', price: 350, title: { es: 'Gafas de sol', en: 'Sunglasses' } },
  { id: 'corazon', slot: 'glasses', price: 550, title: { es: 'Gafas de corazón', en: 'Heart glasses' } },
  { id: 'persianas', slot: 'glasses', price: 900, title: { es: 'Gafas de persiana (los 80)', en: '80s shutter shades' } },
  { id: 'persianas-rosa', slot: 'glasses', price: 50, base: 'persianas', title: { es: 'Persianas rosa', en: 'Pink shutters' } },
  { id: 'persianas-naranja', slot: 'glasses', price: 50, base: 'persianas', title: { es: 'Persianas naranja', en: 'Orange shutters' } },
  { id: 'persianas-azul', slot: 'glasses', price: 50, base: 'persianas', title: { es: 'Persianas azul', en: 'Blue shutters' } },
  { id: 'persianas-blanco', slot: 'glasses', price: 50, base: 'persianas', title: { es: 'Persianas blancas', en: 'White shutters' } },
  { id: 'corazon-rojo', slot: 'glasses', price: 50, base: 'corazon', title: { es: 'Corazón rojo', en: 'Red hearts' } },
  { id: 'corazon-morado', slot: 'glasses', price: 50, base: 'corazon', title: { es: 'Corazón morado', en: 'Purple hearts' } },
  { id: 'corazon-dorado', slot: 'glasses', price: 50, base: 'corazon', title: { es: 'Corazón dorado', en: 'Gold hearts' } },

  { id: 'pajarita', slot: 'extra', price: 250, title: { es: 'Corbatín', en: 'Bow tie' } },
  { id: 'capa', slot: 'extra', price: 1100, title: { es: 'Capa de superhéroe', en: 'Superhero cape' } },
  { id: 'cadena', slot: 'extra', price: 2000, title: { es: 'Cadena de oro con bling', en: 'Blinged-out gold chain' } },

  { id: 'amarillo', slot: 'color', price: 0, title: { es: 'Amarillo', en: 'Yellow' } },
  { id: 'azul', slot: 'color', price: 150, title: { es: 'Azul', en: 'Blue' } },
  { id: 'rosa', slot: 'color', price: 200, title: { es: 'Rosa', en: 'Pink' } },
  { id: 'coqui', slot: 'color', price: 350, title: { es: 'Verde coquí', en: 'Coquí green' } },
  { id: 'morado', slot: 'color', price: 500, title: { es: 'Morado', en: 'Purple' } },
  { id: 'arcoiris', slot: 'color', price: 1500, title: { es: 'Arcoíris', en: 'Rainbow' } },
  { id: 'oro', slot: 'color', price: 3000, title: { es: 'Oro puro', en: 'Solid gold' } },

  { id: 'cresta', slot: 'hair', price: 250, title: { es: 'Cresta', en: 'Mohawk' } },
  { id: 'rizos', slot: 'hair', price: 400, title: { es: 'Rizos', en: 'Curls' } },
  { id: 'trenzas', slot: 'hair', price: 500, title: { es: 'Trenzas', en: 'Braids' } },
  { id: 'afro', slot: 'hair', price: 650, title: { es: 'Afro', en: 'Afro' } },
  { id: 'mullet', slot: 'hair', price: 750, title: { es: 'Mullet ochentoso', en: '80s mullet' } },
  { id: 'peluca', slot: 'hair', price: 1000, title: { es: 'Peluca rosa', en: 'Pink wig' } },

  { id: 'surf', slot: 'ride', price: 2500, title: { es: 'Tabla de surf', en: 'Surfboard' } },
  { id: 'jetski', slot: 'ride', price: 3500, title: { es: 'Jet ski', en: 'Jet ski' } },
  { id: 'lancha', slot: 'ride', price: 4000, title: { es: 'Lancha de pesca', en: 'Fishing boat' } },
  { id: 'fiesta', slot: 'ride', price: 5500, title: { es: 'Bote de fiesta', en: 'Party boat' } },
  { id: 'motora', slot: 'ride', price: 6500, title: { es: 'Motora', en: 'Motorcycle' } },
  { id: 'dirtbike', slot: 'ride', price: 7000, title: { es: 'Dirt bike', en: 'Dirt bike' } },
  { id: 'fourtrack', slot: 'ride', price: 8000, title: { es: 'Four-track', en: 'ATV' } },

  { id: 'mar', slot: 'bg', price: 0, title: { es: 'El mar', en: 'The sea' } },
  { id: 'atardecer', slot: 'bg', price: 200, title: { es: 'Atardecer', en: 'Sunset' } },
  { id: 'yunque', slot: 'bg', price: 350, title: { es: 'El Yunque', en: 'El Yunque' } },
  { id: 'bandera', slot: 'bg', price: 500, title: { es: 'La bandera', en: 'The flag' } },
  { id: 'sanjuan', slot: 'bg', price: 800, title: { es: 'Viejo San Juan', en: 'Old San Juan' } },
  { id: 'noche', slot: 'bg', price: 1000, title: { es: 'Noche de coquíes', en: 'Coquí night' } },
  { id: 'crashboat', slot: 'bg', price: 1400, title: { es: 'Crash Boat', en: 'Crash Boat' } },
  { id: 'bio', slot: 'bg', price: 1800, title: { es: 'Bahía Bioluminiscente', en: 'Bioluminescent Bay' } },
  { id: 'parranda', slot: 'bg', price: 2500, title: { es: 'Parranda', en: 'Parranda' } },
  { id: 'sansebastian', slot: 'bg', price: 3000, title: { es: 'Fiestas de la Calle San Sebastián', en: 'San Sebastián Street Festival' } },
  { id: 'fuegos', slot: 'bg', price: 3500, title: { es: 'Fuegos artificiales', en: 'Fireworks' } },
]

export const DEFAULTS: Partial<Record<Slot, string>> = { color: 'amarillo', bg: 'mar' }

export type Look = Partial<Record<Slot, string>>

export function itemById(id: string): Item | null {
  return ITEMS.find((i) => i.id === id) ?? null
}

/** Free items are owned by everybody. */
export function owns(owned: string[], id: string): boolean {
  const it = itemById(id)
  return (it?.price === 0 && !it.exclusive) || owned.includes(id)
}

export const COLORS: Record<string, { body: string; wing: string; belly: string }> = {
  amarillo: { body: '#ffd23f', wing: '#ffb800', belly: '#fff1b8' },
  azul: { body: '#5aa9ff', wing: '#2f7fe6', belly: '#dcecff' },
  rosa: { body: '#ff8fc7', wing: '#f062a8', belly: '#ffe1f0' },
  coqui: { body: '#6ee7a0', wing: '#2fbf6f', belly: '#dcfbe8' },
  morado: { body: '#b18cff', wing: '#8b5cf6', belly: '#eee4ff' },
  oro: { body: '#ffcf33', wing: '#e0a100', belly: '#e8b400' },
  // Drawn as a gradient by Pichi; these are the fallbacks.
  arcoiris: { body: '#ff8fc7', wing: '#5aa9ff', belly: '#ffffff' },
}

export const SLOT_IDS: Slot[] = ['hat', 'hair', 'glasses', 'extra', 'color', 'bg', 'ride']

/** Lens/frame colours for the glasses that come in variants. */
export const TINTS: Record<string, string> = {
  persianas: '#a3e635', 'persianas-rosa': '#ff4fa3', 'persianas-naranja': '#ff7a1a', 'persianas-azul': '#38bdf8', 'persianas-blanco': '#ffffff',
  corazon: '#ff4fa3', 'corazon-rojo': '#ef233c', 'corazon-morado': '#a855f7', 'corazon-dorado': '#ffcf33',
}
